import type {
  AddOn as AddOnData,
  AddOnProductUnitName,
} from "schemas/generated/addOns";
import type {
  BillingInterval,
  ProductUnitName,
} from "schemas/generated/billing";

import { exhaustiveSwitch } from "replo-utils/lib/misc";
import * as z from "zod";

export const ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME =
  "additional.shopify.theme.project";
export const ADDITIONAL_SHOPIFY_THEME_PROJECT_PRICE_PER_MONTH = 99;

export const ADDITIONAL_REPLO_SITE_PRODUCT_UNIT_NAME = "additional.replo.site";
export const ADDITIONAL_REPLO_SITE_PRICE_PER_MONTH = 99;

export const ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME =
  "v1.additional.published.elements";
export const ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_SIZE = 20;
export const ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_PRICE_PER_MONTH = 99;
export const MAX_ADDITIONAL_PUBLISHED_ELEMENT_BUNDLES = 2;

export const addOnProductUnitNameSchema = z
  .enum([
    ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME,
    ADDITIONAL_REPLO_SITE_PRODUCT_UNIT_NAME,
    ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME,
  ])
  .describe("AddOnProductUnitName");

export const addOnSchema = z
  .object({
    type: addOnProductUnitNameSchema,
    quantity: z.number().int().min(0),
  })
  .describe("AddOn");

export class AddOn {
  readonly type: AddOnProductUnitName;
  readonly quantity: number;

  constructor({
    type,
    quantity,
  }: {
    type: AddOnProductUnitName;
    quantity: number;
  }) {
    this.type = type;
    this.quantity = quantity;
  }

  static fromData(addOn: AddOnData): AddOn {
    return new AddOn(addOn);
  }

  static fromList(addOns: AddOnData[]): AddOn[] {
    return addOns.map((addOn) => AddOn.fromData(addOn));
  }

  static buildFromCounts({
    additionalSites,
    additionalProjects,
    additionalPublishedElementBundles,
  }: {
    additionalSites: number;
    additionalProjects: number;
    additionalPublishedElementBundles: number;
  }): AddOnData[] {
    const addOns: AddOnData[] = [];
    if (additionalSites > 0) {
      addOns.push(
        new AddOn({
          type: ADDITIONAL_REPLO_SITE_PRODUCT_UNIT_NAME,
          quantity: additionalSites,
        }).toData(),
      );
    }
    if (additionalProjects > 0) {
      addOns.push(
        new AddOn({
          type: ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME,
          quantity: additionalProjects,
        }).toData(),
      );
    }
    if (additionalPublishedElementBundles > 0) {
      addOns.push(
        new AddOn({
          type: ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME,
          quantity: additionalPublishedElementBundles,
        }).toData(),
      );
    }
    return addOns;
  }

  static buildFromProductUnits({
    productUnits,
  }: {
    productUnits: { name: string }[];
  }): AddOnData[] {
    const addOnsByType = new Map<AddOnData["type"], number>();
    for (const unit of productUnits) {
      const parsedType = addOnProductUnitNameSchema.safeParse(unit.name);
      if (!parsedType.success) {
        continue;
      }
      addOnsByType.set(
        parsedType.data,
        (addOnsByType.get(parsedType.data) ?? 0) + 1,
      );
    }
    return Array.from(addOnsByType.entries()).map(([type, quantity]) => {
      return new AddOn({ type, quantity }).toData();
    });
  }

  private static normalizeList(addOns: AddOnData[] | AddOn[]): AddOn[] {
    if (addOns.length === 0) {
      return [];
    }
    return addOns[0] instanceof AddOn
      ? // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        (addOns as AddOn[])
      : // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        AddOn.fromList(addOns as AddOnData[]);
  }

  // Given a type, return the quantity of that type in the addOns.
  static getQuantity({
    addOns,
    type,
  }: {
    addOns: AddOnData[] | AddOn[];
    type: AddOnProductUnitName;
  }): number {
    return AddOn.normalizeList(addOns)
      .filter((addOn) => addOn.type === type)
      .reduce((acc, addOn) => acc + addOn.quantity, 0);
  }

  static totalPricePerBillingInterval({
    addOns,
    billingInterval,
  }: {
    addOns: AddOnData[] | AddOn[];
    billingInterval: BillingInterval;
  }): number {
    return AddOn.normalizeList(addOns).reduce((total, addOn) => {
      return total + addOn.pricePerBillingInterval(billingInterval);
    }, 0);
  }

  toData(): AddOnData {
    return { type: this.type, quantity: this.quantity };
  }

  private unitPricePerMonth(): number {
    return exhaustiveSwitch({ type: this.type })({
      [ADDITIONAL_SHOPIFY_THEME_PROJECT_PRODUCT_UNIT_NAME]: () => {
        return ADDITIONAL_SHOPIFY_THEME_PROJECT_PRICE_PER_MONTH;
      },
      [ADDITIONAL_REPLO_SITE_PRODUCT_UNIT_NAME]: () => {
        return ADDITIONAL_REPLO_SITE_PRICE_PER_MONTH;
      },
      [ADDITIONAL_PUBLISHED_ELEMENTS_BUNDLE_PRODUCT_UNIT_NAME]: () => {
        return ADDITIONAL_PUBLISHED_ELEMENT_BUNDLE_PRICE_PER_MONTH;
      },
    });
  }

  pricePerMonth(): number {
    return this.unitPricePerMonth() * this.quantity;
  }

  pricePerBillingInterval(billingInterval: BillingInterval): number {
    if (billingInterval === "monthly") {
      return this.pricePerMonth();
    }

    return this.pricePerMonth() * 12;
  }

  toProductUnits({ startedAt }: { startedAt: Date | null }): {
    name: ProductUnitName;
    startedAt: Date | null;
    monthlyAmount: number;
    yearlyAmount: number;
  }[] {
    const unitPricePerMonth = this.unitPricePerMonth();
    const unit = {
      name: this.type,
      startedAt,
      monthlyAmount: unitPricePerMonth,
      yearlyAmount: unitPricePerMonth * 12,
    };

    return Array.from({ length: this.quantity }, () => ({ ...unit }));
  }
}
